import {
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
  useTheme,
} from "@mui/material";
import { Column, SortingState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useLocalization } from "../../hooks/useLocalization";
import { ConfirmationDialog } from "../ConfirmationDialog";

interface SortModalProps<T extends object> {
  isOpen: boolean;
  onClose: () => void;
  columns: Column<T>[];
  sorting: SortingState;
  onSortingChange?: (sorting: SortingState) => void;
}

export const SortModal = <T extends object>({
  isOpen,
  onClose,
  columns,
  sorting,
  onSortingChange,
}: SortModalProps<T>): React.ReactElement => {
  const [tempSorting, setTempSorting] = useState<SortingState>([]);

  const { t } = useLocalization();
  const theme = useTheme();

  const sortableColumns = columns.filter((column) => column.getCanSort());

  const sortOptions = sortableColumns.flatMap((column) => [
    {
      id: column.id,
      label: String(column.columnDef.header),
      desc: true,
      displayText: `${String(column.columnDef.header)} ${t("components.dataTable.descending")}`,
    },
    {
      id: column.id,
      label: String(column.columnDef.header),
      desc: false,
      displayText: `${String(column.columnDef.header)} ${t("components.dataTable.ascending")}`,
    },
  ]);

  useEffect(() => {
    if (isOpen) {
      setTempSorting([...sorting]);
    }
  }, [isOpen, sorting]);

  const handleSortOptionClick = (columnId: string, desc: boolean) => {
    setTempSorting([{ id: columnId, desc }]);
  };

  const handleApplySort = () => {
    if (onSortingChange) {
      onSortingChange(tempSorting);
    }
    onClose();
  };

  const handleCancelSort = () => {
    setTempSorting([...sorting]);
    onClose();
  };

  const currentSort = tempSorting[0];

  return (
    <ConfirmationDialog.Root
      fullWidth
      maxWidth="xs"
      onClose={handleCancelSort}
      open={isOpen}
      sx={{
        "& .MuiDialog-paper": { padding: "16px", borderRadius: "24px" },
      }}
    >
      <ConfirmationDialog.Title showIcon={false} sx={{ padding: "0px" }}>
        {t("components.dataTable.sortBy")}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          paddingX: "0px",
          mt: 2,
        }}
      >
        {sortOptions.length > 0 &&
          sortOptions.map((option) => {
            const isSelected =
              currentSort?.id === option.id && currentSort.desc === option.desc;

            return (
              <SortOption
                handleSortOptionClick={handleSortOptionClick}
                isSelected={isSelected}
                key={`${option.id}-${option.desc}`}
                option={option}
              />
            );
          })}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions sx={{ padding: "0px" }}>
        <ConfirmationDialog.Button
          onClick={handleCancelSort}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("components.dataTable.cancel")}
        </ConfirmationDialog.Button>
        <ConfirmationDialog.Button
          onClick={handleApplySort}
          variant="contained"
        >
          {t("components.dataTable.apply")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};

interface SortOptionProps {
  option: {
    id: string;
    label: string;
    desc: boolean;
    displayText: string;
  };
  isSelected: boolean;
  handleSortOptionClick: (columnId: string, desc: boolean) => void;
}

const SortOption = ({
  option,
  isSelected,
  handleSortOptionClick,
}: SortOptionProps) => {
  const theme = useTheme();

  return (
    <ListItemButton
      onClick={() => handleSortOptionClick(option.id, option.desc)}
      sx={{
        padding: 0,
        height: 22,
        marginBottom: 1,
        minHeight: "auto",
      }}
    >
      <ListItemIcon sx={{ minWidth: "auto", mr: 2 }}>
        <Radio
          checked={isSelected}
          sx={{
            width: 20,
            color: theme.colors.primary[5],
            fontSize: 20,
            transition: "none",
            "& .MuiSvgIcon-root": {
              transition: "none",
            },
            "&.Mui-checked": {
              transition: "none",
              "& .MuiSvgIcon-root": {
                color: "white",
                borderRadius: "50%",
                paddingLeft: "2px",
                border: `6px solid ${theme.colors.primary[5]}`,
                width: "20px",
                height: "20px",
              },
            },
          }}
        />
      </ListItemIcon>
      <ListItemText
        primary={
          <Typography fontSize={12} fontWeight={500}>
            {option.displayText}
          </Typography>
        }
      />
    </ListItemButton>
  );
};
